import React, { useState } from 'react';
import { FilePdf, FileXls, PencilSimple, Trash, CheckCircle, ArrowsClockwise, Eye, Upload } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';
import { QuotePercentageDialog } from './QuotePercentageDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const InvoiceDetail = ({ item, statusBadge, onBack, onExportPdf, onExportExcel, onEdit, onDelete, onUpdateStatus, onConvertQuote, onCreateCreditNote, invoices, fetchData, setSelectedItem }) => {
  const isQuote = item._viewType === 'devis';
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [submittingConvert, setSubmittingConvert] = useState(false);
  const [previousAdvancements, setPreviousAdvancements] = useState({});

  const openConvertDialog = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/invoicing/quotes/${item.id}/advancements`, { withCredentials: true });
      setPreviousAdvancements(r.data?.previous_advancements || {});
    } catch { setPreviousAdvancements({}); }
    setShowConvertDialog(true);
  };

  const handleConvertWithPercents = async (lineAdvancements) => {
    setSubmittingConvert(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/invoicing/quotes/${item.id}/convert`,
        { line_advancements: lineAdvancements },
        { withCredentials: true }
      );
      toast.success(`Facture ${res.data.number} creee depuis le devis`);
      setShowConvertDialog(false);
      fetchData();
      onBack();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la conversion');
    } finally {
      setSubmittingConvert(false);
    }
  };

  const handleAttachment = async (file, isReplace) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/invoicing/${item.id}/attachment`, fd, { withCredentials: true });
      toast.success(isReplace ? 'Piece jointe mise a jour' : 'Piece jointe ajoutee');
      setSelectedItem(prev => ({ ...prev, attachment_filename: res.data?.filename || 'updated' }));
      fetchData();
    } catch (err) { toast.error('Erreur upload'); }
  };

  return (
    <div data-testid="doc-detail">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <button className="btn btn-secondary btn-sm" onClick={onBack} style={{ marginBottom: '8px' }} data-testid="back-to-list">Retour</button>
          <h1 className="page-title">{isQuote ? 'Devis' : 'Facture'} {item.number}</h1>
          <p className="page-subtitle">{item.project_name} - {item.date}{item.quote_number ? ` (depuis ${item.quote_number})` : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {statusBadge(item.status)}
          {isQuote && item.status === 'brouillon' && <button className="btn btn-primary btn-sm" onClick={() => onUpdateStatus(item.id, 'accepte', 'devis')} data-testid="accept-quote-btn"><CheckCircle size={14} /> Valider</button>}
          {isQuote && item.status === 'brouillon' && <button className="btn btn-danger btn-sm" onClick={() => onUpdateStatus(item.id, 'refuse', 'devis')}>Refuser</button>}
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => onEdit(item, isQuote ? 'devis' : 'facture')}
            data-testid="edit-doc-btn"
            title={isQuote ? 'Modifier ce devis' : 'Modifier cette facture'}
          >
            <PencilSimple size={14} /> Modifier
          </button>
          {isQuote && item.status !== 'refuse' && (
            <button
              className="btn btn-primary btn-sm"
              onClick={openConvertDialog}
              data-testid="convert-quote-btn"
              title="Cree une facture en choisissant un % d'avancement par article"
            >
              <ArrowsClockwise size={14} /> Facturer
            </button>
          )}
          {!isQuote && item.status === 'brouillon' && <button className="btn btn-secondary btn-sm" onClick={() => onUpdateStatus(item.id, 'acceptee')} data-testid="invoice-accept-btn"><CheckCircle size={14} /> Marquer acceptee</button>}
          {!isQuote && (item.status === 'brouillon' || item.status === 'acceptee') && <button className="btn btn-primary btn-sm" onClick={() => onUpdateStatus(item.id, 'emise')}>Emettre</button>}
          {!isQuote && item.status === 'emise' && <button className="btn btn-primary btn-sm" onClick={() => onUpdateStatus(item.id, 'payee')}>Marquer payee</button>}
          {!isQuote && !item.is_credit_note && item.status !== 'brouillon' && (
            <button
              className="btn btn-sm"
              style={{ background: '#FEF3C7', color: '#92400E', borderColor: '#F59E0B' }}
              data-testid="create-credit-note-btn"
              onClick={async () => {
                const reason = window.prompt(`Motif de l'avoir sur ${item.number} (obligation légale) :`, 'Erreur de facturation');
                if (!reason || !reason.trim()) return;
                try {
                  const r = await onCreateCreditNote(item.id, reason.trim());
                  if (r) {
                    // refresh handled by parent
                  }
                } catch (e) { /* parent toasts */ }
              }}
              title="Créer un avoir conforme (mention légale + montants négatifs)"
            >↩ Avoir</button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => onExportPdf(item.id, isQuote ? 'devis' : 'facture')} data-testid="export-pdf-btn"><FilePdf size={14} /> PDF</button>
          {!isQuote && <button className="btn btn-secondary btn-sm" onClick={() => onExportExcel(item.id)}><FileXls size={14} /> Excel</button>}
          {(isQuote || item.status === 'brouillon' || item.is_credit_note) && (
            <button className="btn btn-danger btn-sm" onClick={() => onDelete(item.id, isQuote ? 'devis' : 'facture')} title="Supprimer" data-testid="delete-btn"><Trash size={14} /></button>
          )}
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '24px', gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="stat-card">
          <div className="stat-label">Total HT{!isQuote ? ' (avancement)' : ''}</div>
          <div className="stat-value" style={{ fontSize: '1.5rem' }}>{item.total_ht.toLocaleString('fr-FR')} EUR</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">TVA</div>
          <div className="stat-value" style={{ fontSize: '1.5rem', color: '#EF6C00' }}>{item.total_tva.toLocaleString('fr-FR')} EUR</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total TTC{!isQuote ? ' (avancement)' : ''}</div>
          <div className="stat-value" style={{ fontSize: '1.5rem', color: '#2E7D32' }}>{item.total_ttc.toLocaleString('fr-FR')} EUR</div>
        </div>
      </div>

      {!isQuote && (
        <div className="card" style={{ marginBottom: '16px', padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FilePdf size={20} style={{ color: '#C62828' }} />
              <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>Piece jointe</span>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {item.attachment_filename ? (
                <>
                  <a href={`${API_URL}/api/invoicing/attachment/${item.attachment_filename}`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" data-testid="view-attachment-btn"><Eye size={14} /> Voir</a>
                  <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                    Remplacer
                    <input type="file" accept=".pdf,image/*" style={{ display: 'none' }} onChange={(e) => handleAttachment(e.target.files[0], true)} />
                  </label>
                </>
              ) : (
                <label className="btn btn-primary btn-sm" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }} data-testid="upload-attachment-btn">
                  <Upload size={14} /> Ajouter un PDF
                  <input type="file" accept=".pdf,image/*" style={{ display: 'none' }} onChange={(e) => handleAttachment(e.target.files[0], false)} />
                </label>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" data-testid="doc-lines-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ textAlign: 'center' }}>Unite</th>
                <th style={{ textAlign: 'right' }}>Prix unit.</th>
                <th style={{ textAlign: 'right' }}>Qte</th>
                <th style={{ textAlign: 'right' }}>Prix HT</th>
                <th style={{ textAlign: 'center' }}>TVA %</th>
                <th style={{ textAlign: 'right' }}>Prix TTC</th>
                {!isQuote && <th style={{ textAlign: 'center' }}>Avt mens. %</th>}
                {!isQuote && <th style={{ textAlign: 'center' }}>Avt total %</th>}
                {!isQuote && <th style={{ textAlign: 'right' }}>Avt HT</th>}
                {!isQuote && <th style={{ textAlign: 'right' }}>Avt TTC</th>}
              </tr>
            </thead>
            <tbody>
              {item.lines.map((line, idx) => {
                const kind = (line.kind || 'item');
                if (kind === 'header' || kind === 'note') {
                  const isHeader = kind === 'header';
                  const ncols = isQuote ? 7 : 11;
                  return (
                    <tr key={line.id || idx} data-testid={`detail-special-row-${idx}`}>
                      <td colSpan={ncols} style={{
                        background: isHeader ? '#002FA7' : '#FFFBEB',
                        color: isHeader ? 'white' : '#92400E',
                        fontWeight: isHeader ? 700 : 400,
                        fontStyle: isHeader ? 'normal' : 'italic',
                        padding: '8px 12px',
                      }}>
                        <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', marginRight: 8, opacity: 0.8 }}>
                          {isHeader ? '☰ Catégorie' : '📝 Note'}
                        </span>
                        {line.description}
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={line.id || idx}>
                    <td style={{ fontWeight: '600' }}>{line.description}</td>
                    <td style={{ textAlign: 'center' }}>{line.unit}</td>
                    <td style={{ textAlign: 'right' }}>{line.unit_price?.toLocaleString('fr-FR')} EUR</td>
                    <td style={{ textAlign: 'right' }}>{line.quantity}</td>
                    <td style={{ textAlign: 'right' }}>{line.price_ht?.toLocaleString('fr-FR')} EUR</td>
                    <td style={{ textAlign: 'center' }}>{line.tva_rate}%</td>
                    <td style={{ textAlign: 'right' }}>{line.price_ttc?.toLocaleString('fr-FR')} EUR</td>
                    {!isQuote && <td style={{ textAlign: 'center', fontWeight: '600', color: '#1565C0' }}>{line.monthly_advancement}%</td>}
                    {!isQuote && <td style={{ textAlign: 'center', fontWeight: '700', color: '#002FA7' }}>{line.total_advancement}%</td>}
                    {!isQuote && <td style={{ textAlign: 'right', fontWeight: '600' }}>{line.advancement_ht?.toLocaleString('fr-FR')} EUR</td>}
                    {!isQuote && <td style={{ textAlign: 'right', fontWeight: '700', color: '#2E7D32' }}>{line.advancement_ttc?.toLocaleString('fr-FR')} EUR</td>}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: '#F4F5F7', fontWeight: '700' }}>
                <td colSpan={isQuote ? 4 : 9} style={{ textAlign: 'right' }}>TOTAL</td>
                {isQuote ? (
                  <>
                    <td style={{ textAlign: 'right' }}>{item.total_ht.toLocaleString('fr-FR')} EUR</td>
                    <td></td>
                    <td style={{ textAlign: 'right', color: '#2E7D32' }}>{item.total_ttc.toLocaleString('fr-FR')} EUR</td>
                  </>
                ) : (
                  <>
                    <td style={{ textAlign: 'right' }}>{item.total_ht.toLocaleString('fr-FR')} EUR</td>
                    <td style={{ textAlign: 'right', color: '#2E7D32' }}>{item.total_ttc.toLocaleString('fr-FR')} EUR</td>
                  </>
                )}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      {item.notes && <div style={{ marginTop: '16px', padding: '12px 16px', backgroundColor: '#F4F5F7', borderRadius: '4px', fontSize: '0.9rem', color: '#5E5F66' }}>{item.notes}</div>}

      {showConvertDialog && (
        <QuotePercentageDialog
          quote={item}
          previousAdvancements={previousAdvancements}
          onClose={() => setShowConvertDialog(false)}
          onConfirm={handleConvertWithPercents}
          submitting={submittingConvert}
        />
      )}
    </div>
  );
};
